/**
 * App-layer session policy (Cognito pool settings are unchanged).
 *
 * Cognito defaults (documented baseline):
 * - Access token ~1 hour (Amplify refreshes automatically)
 * - Refresh token ~30 days (pool default; not modified here)
 *
 * This module adds defense-in-depth idle and absolute caps so stale devices
 * must re-authenticate even if a refresh token remains valid.
 */
export const SESSION_POLICY = {
  cognitoAccessTokenMinutes: 60,
  cognitoRefreshTokenDays: 30,
  /** Web: force re-sign-in after this idle period (user-requested ~4 days). */
  webIdleDays: 4,
  /** Native: longer idle when biometric lock guards foreground return. */
  nativeIdleDays: 7,
  /** Hard stop — matches refresh-token ceiling; requires full sign-in. */
  absoluteMaxDays: 30,
} as const

export type SessionExpiryReason = 'idle' | 'absolute'

export function idleTimeoutMs(isNative: boolean): number {
  const days = isNative ? SESSION_POLICY.nativeIdleDays : SESSION_POLICY.webIdleDays
  return days * 24 * 60 * 60 * 1000
}

export function absoluteTimeoutMs(): number {
  return SESSION_POLICY.absoluteMaxDays * 24 * 60 * 60 * 1000
}

export function sessionExpiredMessage(reason: SessionExpiryReason): string {
  if (reason === 'absolute') {
    return 'Your session has reached the maximum length. Please sign in again.'
  }
  return 'Your session expired due to inactivity. Please sign in again.'
}
